import {Component, HostListener, OnInit} from '@angular/core';
import {AllergenGroup, DisplayedAllergenGroup, MenuSection, OverridePositionsMap, OverridePosition, PositionTypeEnum} from 'app/models/menu.model';
import {AllergenGroupApiService} from 'app/services/menu-edit/allergen-group-api.service';
import {ActivatedRoute} from '@angular/router';
import {firstValueFrom, Observable} from 'rxjs';
import {Select} from '@ngxs/store';
import {OrganizationsState} from 'app/state-management/states/organizations.states';
import {Organization} from 'app/models/organization.model';
import {UntilDestroy} from '@ngneat/until-destroy';
import {TranslateService} from '@ngx-translate/core';
import {Store} from '@ngrx/store';
import {setAllergenGroups} from 'app/reducers/menu.actions';
import {DishesService} from 'app/services/dishes.service';
import {MenuOverrideService} from 'app/services/menu-override.service';
import {
  AllergenGroupFormDataType,
} from 'app/pages/menu-edit/allergen-groups/allergen-group-form/allergen-group-form.component';
import { GetPosPermissionCode } from 'app/helpers/permission-codes';
import { PermissionService } from 'app/services/permission.service';

interface AllergenGroupFormResult {
  created?: DisplayedAllergenGroup;
  updated?: DisplayedAllergenGroup;
  deleted?: string;
}

@UntilDestroy()
@Component({
  selector: 'app-allergen-groups',
  templateUrl: './allergen-groups.component.html',
  styleUrls: ['./allergen-groups.component.scss']
})
export class AllergenGroupsComponent implements OnInit {
  allergenGroups: DisplayedAllergenGroup[] = [];
  originalAllergenGroups: DisplayedAllergenGroup[] = [];
  organization: Organization;
  loading = false;
  error: string | null = null;
  allergenGroupsMap: Map<string, DisplayedAllergenGroup> = new Map();
  originalAllergenGroupsMap: Map<string, DisplayedAllergenGroup> = new Map();
  menuId = '';
  menuSections: MenuSection[] = [];
  selectedMenuSectionId = '';
  allergenGroupsCache: Map<string, DisplayedAllergenGroup[]> = new Map();
  overridesCache: Map<string, OverridePosition[]> = new Map();

  formData: AllergenGroupFormDataType | null = null;
  public hasProductEdit$ = this.permissions.hasPermission$(GetPosPermissionCode.GetPosProductEdit);

  @Select(OrganizationsState.getSelectedOrganization)
  selectedOrganization$: Observable<Organization>;
  private allOverrides: OverridePosition[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private allergenGroupApiService: AllergenGroupApiService,
    private translate: TranslateService,
    private store: Store,
    private dishesService: DishesService,
    private permissions: PermissionService,
  ) {
  }

  ngOnInit(): void {
    this.getOrganizationAndData();
  }

  async getOrganizationAndData(): Promise<void> {
    try {
      this.loading = true;
      this.error = null;
      this.organization = await firstValueFrom(this.selectedOrganization$);

      if (!this.organization?.id) {
        throw new Error('Organization not found');
      }

      const overridesResponse = await this.dishesService.getOverridesByOrganizationAndType(this.organization.id, PositionTypeEnum.AllergenGroups);
      this.allOverrides = overridesResponse.data ?? [];

      this.menuId = await this.dishesService.getMenuIdByOrganization(this.organization.id);

      if (!this.menuId) {
        throw new Error('Menu ID not found');
      }

      const menuSectionsResponse = await this.dishesService.getMenuSections(this.organization.id);
      this.menuSections = menuSectionsResponse.data ?? [];

      if (this.menuSections.length > 0 && !this.selectedMenuSectionId) {
        this.selectedMenuSectionId = this.menuSections[0].id;
      }

      const allergenGroups = await this.allergenGroupApiService.getAllergenGroupsAsync(this.organization.id);
      const rawAllergenGroups = allergenGroups ?? [];

      this.originalAllergenGroups = rawAllergenGroups.map(ag => ({...ag}));

      await this.applyOverridesForCurrentSection();
    } catch (error) {
      console.error('Failed to initialize allergen groups:', error);
      this.error = this.translate.instant('MENU.ALLERGEN_GROUP.LOAD_ERROR');
      this.allergenGroups = [];
      this.updateMaps();
    } finally {
      this.loading = false;
    }
  }

  private async applyOverridesForCurrentSection(): Promise<void> {
    if (!this.organization?.id || !this.selectedMenuSectionId) {
      return;
    }

    const cachedGroups = this.allergenGroupsCache.get(this.selectedMenuSectionId);
    if (cachedGroups) {
      this.allergenGroups = cachedGroups;
      this.updateMaps();
      return;
    }

    try {
      let filteredOverrides = this.overridesCache.get(this.selectedMenuSectionId);

      if (!filteredOverrides) {
        filteredOverrides = this.allOverrides.filter(o => o.menuSectionId === this.selectedMenuSectionId);
        this.overridesCache.set(this.selectedMenuSectionId, filteredOverrides);
      }

      const overridePositionsMap = new OverridePositionsMap(filteredOverrides);
      const overriddenGroups = this.originalAllergenGroups.map(ag => MenuOverrideService.overrideAllergenGroups({...ag}, overridePositionsMap));

      this.allergenGroupsCache.set(this.selectedMenuSectionId, overriddenGroups);
      this.allergenGroups = overriddenGroups;
      this.updateMaps();
    } catch (error) {
      console.error('Failed to apply overrides:', error);
    }
  }

  getSelectedTabIndex(): number {
    return this.menuSections.findIndex(s => s.id === this.selectedMenuSectionId);
  }

  async onTabChange(event: { index: number }): Promise<void> {
    const section = this.menuSections[event.index];
    if (section) {
      this.selectedMenuSectionId = section.id;
      this.formData = null;
      await this.applyOverridesForCurrentSection();
    }
  }

  private updateMaps(): void {
    this.allergenGroupsMap.clear();
    this.originalAllergenGroupsMap.clear();
    for (const allergenGroup of this.allergenGroups) {
      this.allergenGroupsMap.set(allergenGroup.id, allergenGroup);
    }
    for (const originalGroup of this.originalAllergenGroups) {
      this.originalAllergenGroupsMap.set(originalGroup.id, originalGroup);
    }
    this.store.dispatch(setAllergenGroups([...this.allergenGroupsMap.values()]));
  }

  async onClickCreateAllergenGroup(): Promise<void> {
    if (!await firstValueFrom(this.hasProductEdit$)) {
      return;
    }
    if (!this.menuId) {
      this.error = this.translate.instant('MENU.ALLERGEN_GROUP.MENU_ID_MISSING');
      return;
    }

    const emptyAllergenGroup: AllergenGroup = {
      name: '',
      code: '',
      nameTranslations: {},
      isManuallyCreated: true,
      externalPictureUrl: '',
      textColor: '',
      backgroundColor: '',
    };

    this.formData = this.buildFormData(null, emptyAllergenGroup, emptyAllergenGroup);
  }

  async onEditAllergenGroup(allergenGroupId: string): Promise<void> {
    if (!await firstValueFrom(this.hasProductEdit$)) {
      return;
    }
    if (!this.menuId) {
      this.error = this.translate.instant('MENU.ALLERGEN_GROUP.MENU_ID_MISSING');
      return;
    }

    const allergenGroup = this.allergenGroupsMap.get(allergenGroupId);
    const originalAllergenGroup = this.originalAllergenGroupsMap.get(allergenGroupId);

    if (!allergenGroup) {
      return;
    }

    this.formData = this.buildFormData(
      allergenGroupId,
      {...allergenGroup},
      {...(originalAllergenGroup ?? allergenGroup)},
    );
  }

  private buildFormData(
    allergenGroupId: string | null,
    allergenGroup: AllergenGroup,
    originalAllergenGroup: AllergenGroup,
  ): AllergenGroupFormDataType {
    return {
      allergenGroupId,
      allergenGroup,
      originalAllergenGroup,
      organization: this.organization,
      menuSectionId: this.selectedMenuSectionId,
      menuId: this.menuId,
      allowOverrides: true,
      allergenGroups: [...this.allergenGroupsMap.values()],
      menuSections: this.menuSections,
    };
  }

  async onFormResult(res: AllergenGroupFormResult | null): Promise<void> {
    if (!res) {
      this.formData = null;
      return;
    }

    if (res.created) {
      const created = res.created;
      this.allergenGroupsMap.set(created.id, created);
      this.allergenGroups = [...this.allergenGroupsMap.values()];
      this.originalAllergenGroupsMap.set(created.id, {...created});
      this.originalAllergenGroups = [...this.originalAllergenGroups, created];
    } else if (res.updated) {
      const updated = res.updated;
      this.allergenGroupsMap.set(updated.id, updated);
      this.allergenGroups = [...this.allergenGroupsMap.values()];
      if (!updated.externalId) {
        this.originalAllergenGroupsMap.set(updated.id, {...updated});
        const idx = this.originalAllergenGroups.findIndex(ag => ag.id === updated.id);
        if (idx >= 0) {
          this.originalAllergenGroups[idx] = updated;
        }
      }
    } else if (res.deleted) {
      const deletedId = res.deleted;
      this.allergenGroupsMap.delete(deletedId);
      this.allergenGroups = [...this.allergenGroupsMap.values()];
      this.originalAllergenGroupsMap.delete(deletedId);
      this.originalAllergenGroups = this.originalAllergenGroups.filter(ag => ag.id !== deletedId);
    }

    this.store.dispatch(setAllergenGroups([...this.allergenGroupsMap.values()]));
    this.formData = null;

    await this.refreshOverridesForCurrentSection();
  }

  onFormClose(): void {
    this.formData = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.formData) return;
    const target = event.target as HTMLElement;
    const formPanel = document.querySelector('.form-panel');
    if (formPanel?.contains(target)) return;
    if (target.closest('.allergen-group-item') || target.closest('button')) return;
    this.onFormClose();
  }

  async onDeleteAllergenGroup(event: MouseEvent, allergenGroupId: string): Promise<void> {
    event.stopPropagation();
    if (!await firstValueFrom(this.hasProductEdit$)) {
      return;
    }

    if (!this.menuId) {
      this.error = this.translate.instant('MENU.ALLERGEN_GROUP.MENU_ID_MISSING');
      return;
    }

    if (!confirm(this.translate.instant('MENU.ALLERGEN_GROUP.CONFIRM_DELETE'))) {
      return;
    }

    try {
      this.loading = true;
      this.error = null;
      await this.allergenGroupApiService.delete(allergenGroupId);
      this.allergenGroupsMap.delete(allergenGroupId);
      this.allergenGroups = [...this.allergenGroupsMap.values()];
      this.originalAllergenGroupsMap.delete(allergenGroupId);
      this.store.dispatch(setAllergenGroups([...this.allergenGroupsMap.values()]));
    } catch (error) {
      console.error('Failed to delete allergen group:', error);
      this.error = this.translate.instant('MENU.ALLERGEN_GROUP.DELETE_ERROR');
    } finally {
      this.loading = false;
    }
  }

  isManuallyCreated(allergenGroupId: string): boolean {
    const allergenGroup = this.allergenGroupsMap.get(allergenGroupId);
    return !allergenGroup?.externalId;
  }

  hasOverridesInCurrentSection(allergenGroupId: string): boolean {
    const sectionOverrides = this.overridesCache.get(this.selectedMenuSectionId);
    if (!sectionOverrides) {
      return false;
    }
    return sectionOverrides.some(o => o.positionId === allergenGroupId);
  }

  private async refreshOverridesForCurrentSection(): Promise<void> {
    this.allergenGroupsCache.delete(this.selectedMenuSectionId);
    this.overridesCache.delete(this.selectedMenuSectionId);

    try {
      const response = await this.dishesService.getOverridesByOrganizationAndType(this.organization.id, PositionTypeEnum.AllergenGroups);
      this.allOverrides = response.data ?? [];
    } catch (error) {
      console.error('Failed to refresh overrides:', error);
    }

    await this.applyOverridesForCurrentSection();
  }
}
